/**
 * Settings a NEW project starts from, on top of DEFAULT_CONFIG.
 *
 * DEFAULT_CONFIG is merged under every saved project when it is loaded, so a
 * change there would quietly change existing studies. These are applied once,
 * when a project is created, and then belong to it like any other setting.
 * - Auto-design capacity: ±10% around the requested target; saved projects keep
 *   their own upper ratio and the historical 99% lower ratio when omitted.
 *
 * - Cell temperature: the U-value thermal balance (Uc, Uv), the model most
 *   comparative studies use, with the thermal preset following the project's
 *   installation type ("auto"): free standing 29, frames on a flat roof 20,
 *   flush on the roof 15 W/m²K, and Uv = 0, so no wind cooling is credited.
 *   SAPM is set to follow the installation type as well, should the user
 *   switch model. Either can be overridden in the Modelling tab.
 * - Degradation: a warranty curve of 1% in the first year and 0.4% a year
 *   after it, over 30 years, instead of the legacy single 1.5% a year.
 */
import { linearRetention } from "../../utils/degradationCurve";
import { NEW_CAPACITY_RATIOS } from "./autoDesignTolerance";

export const NEW_PROJECT_THERMAL_MODEL = "pvsyst";
export const NEW_PROJECT_THERMAL_SETTINGS = { pvsyst: { mode: "auto" }, sapm: { mode: "auto" } };

/** True when every model is left to follow the installation type (key order free). */
export function isNewProjectThermalSettings(settings) {
  const keys = Object.keys(settings || {});
  const wanted = Object.keys(NEW_PROJECT_THERMAL_SETTINGS);
  return keys.length === wanted.length && wanted.every((key) =>
    settings[key] && Object.keys(settings[key]).length === 1 && settings[key].mode === "auto");
}
export const NEW_PROJECT_DEGRADATION = { firstYearPct: 1, annualPct: 0.4, years: 30 };

export function withNewProjectDefaults(base) {
  const cfg = base || {};
  const { firstYearPct, annualPct, years } = NEW_PROJECT_DEGRADATION;
  return {
    ...cfg,
    autoDesign: { ...cfg.autoDesign, ...NEW_CAPACITY_RATIOS },
    panelOrientation: {
      ...(cfg.panelOrientation || {}),
      thermalModel: NEW_PROJECT_THERMAL_MODEL,
      thermalSettings: JSON.parse(JSON.stringify(NEW_PROJECT_THERMAL_SETTINGS)),
    },
    pvDegradation: { retentionPct: linearRetention(firstYearPct, annualPct, years) },
    annualDegFactor: annualPct,
    pvLosses: {
      ...(cfg.pvLosses || {}),
      dc: { ...(cfg.pvLosses?.dc || {}), lid: firstYearPct, age: 0 },
    },
  };
}
